import { threadPullRequestKeysEqual, visibleThreadPullRequests } from "@afar-research/afar";
import type { EnvironmentId, ThreadPullRequestLink } from "@afar-research/contracts";

interface ThreadWithPullRequests {
  readonly environmentId: EnvironmentId;
  readonly pullRequests?: ReadonlyArray<ThreadPullRequestLink> | undefined;
}

/**
 * The membership an open pull request tab can wear its state from. A pull request opened in
 * the panel is usually one some thread in the same Environment already links, and that link
 * carries what the Environment last read — so the tab states itself without asking the host
 * again. Two threads may link the same pull request; the most recently observed one wins,
 * since it is the one whose state is least stale.
 */
export function resolvePullRequestTabLink(
  threads: ReadonlyArray<ThreadWithPullRequests>,
  environmentId: EnvironmentId | null,
  host: string | null,
  reference: { readonly repository: string; readonly number: number },
): ThreadPullRequestLink | undefined {
  if (environmentId === null || host === null) return undefined;
  let newest: ThreadPullRequestLink | undefined;
  for (const thread of threads) {
    if (thread.environmentId !== environmentId) continue;
    for (const link of visibleThreadPullRequests(thread.pullRequests ?? [])) {
      if (
        !threadPullRequestKeysEqual(link, {
          host,
          repository: reference.repository,
          number: reference.number,
        })
      ) {
        continue;
      }
      if (
        newest === undefined ||
        (link.observation?.observedAt ?? "") > (newest.observation?.observedAt ?? "")
      ) {
        newest = link;
      }
    }
  }
  return newest;
}
